import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronRight, Brain } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { t } from '../../i18n';
import { supabase } from '../../services/authService';
import { MatchPrediction } from '../../types';
import SEO from '../Common/SEO';
import { getTeamName } from '../../services/teamService';
import { getLeagueName } from '../../services/leagueService';

export const MatchAnalysisListView: React.FC = () => {
  const { lang } = useLanguage();
  const [posts, setPosts] = useState<MatchPrediction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAnalysisData = async () => {
      setLoading(true);
      try {
        const now = new Date().toISOString();
        const { data: predictions, error } = await supabase
            .from('match_predictions')
            .select('*')
            .gt('matchdate', now)
            .order('matchdate', { ascending: true });

        if (!error && predictions) {
            // post_id 중복 제거 (최신순 유지)
            const uniquePredictions = predictions.reduce((acc: any[], current) => {
              const x = acc.find(item => item.post_id === current.post_id);
              if (!x) {
                return acc.concat([current]);
              } else {
                return acc;
              }
            }, []);

            const fetchedPosts = uniquePredictions.map(item => {
                const homeId = item.home_team_id;
                const awayId = item.away_team_id;
                const leagueId = item.league_id;
                
                const homeName = item.home_team_name || getTeamName(homeId, lang, homeId ? `Team ${homeId}` : 'Home');
                const awayName = item.away_team_name || getTeamName(awayId, lang, awayId ? `Team ${awayId}` : 'Away');
                const leagueName = leagueId ? getLeagueName(Number(leagueId), lang) : 'Analysis';

                const homeForm = item.home_form || 'WDLWD';
                const awayForm = item.away_form || 'LLWWD';

                return {
                    ...item,
                    id: item.post_id,
                    match_info: {
                        homeTeam: { 
                          name: homeName, 
                          id: homeId, 
                          logo: `https://media.api-sports.io/football/teams/${homeId}.png`,
                          form: homeForm.split('')
                        },
                        awayTeam: { 
                          name: awayName, 
                          id: awayId, 
                          logo: `https://media.api-sports.io/football/teams/${awayId}.png`,
                          form: awayForm.split('')
                        },
                        leagueName: leagueName,
                        leagueId: leagueId,
                        fixtureDate: item.matchdate || item.match_date || item.fixture_date || item.created_at
                    }
                };
            }) as MatchPrediction[];
            setPosts(fetchedPosts);
        }
      } catch (error) {
        console.error("Failed to load analysis posts:", error);
      } finally {
        setLoading(false);
      }
    };
    loadAnalysisData();
  }, [lang]);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={`${t(lang, 'matchAnalysis')} | XLEVEN`}
        description="Expert match breakdown & insights from the XLEVEN community"
      />
      
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-50 px-4 h-14 flex items-center gap-4">
        <button onClick={() => window.history.back()} className="p-2 text-slate-900 hover:bg-slate-50 rounded-full transition-colors">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-sm font-black text-slate-900 uppercase tracking-tight">{t(lang, 'matchAnalysis')}</h1>
      </header>

      <div className="max-w-5xl mx-auto p-4 md:p-6 pb-20">

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-slate-50 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : posts.length > 0 ? (
          <div className="divide-y divide-gray-100 border-b border-gray-100">
            {posts.map((post, index) => {
              const d = post.match_info?.fixtureDate || post.created_at || '';
              const dateObjForId = d.includes('T') || d.includes('Z') ? new Date(d) : new Date(d.replace(' ', 'T') + '+09:00');
              const dateStr = dateObjForId.toLocaleDateString('sv-SE');
              
              // Check if this is the first post of this date to assign ID for scrolling
              const isFirstOfDate = index === 0 || (() => {
                const prevPost = posts[index - 1];
                const prevD = prevPost.match_info?.fixtureDate || prevPost.created_at || '';
                const prevDateObj = prevD.includes('T') || prevD.includes('Z') ? new Date(prevD) : new Date(prevD.replace(' ', 'T') + '+09:00');
                const prevDateStr = prevDateObj.toLocaleDateString('sv-SE');
                return prevDateStr !== dateStr;
              })();

              return (
                <div 
                  key={post.id} 
                  id={isFirstOfDate ? `date-${dateStr}` : undefined}
                  onClick={() => window.navigateTo(`/game/${post.id}`)}
                  className="group relative bg-white flex items-center py-5 transition-all cursor-pointer px-2 scroll-mt-32"
                >
                  <div className="flex-1 flex flex-col gap-3 min-w-0">
                    {/* League Name and Date/Time */}
                    <div className="flex items-center gap-3">
                      <div className="text-[11px] font-black text-slate-400 uppercase tracking-tight">
                        {post.match_info?.leagueName}
                      </div>
                      <div className="w-1 h-1 rounded-full bg-slate-200" />
                      <div className="flex items-center gap-2">
                        <div className="text-[11px] font-bold text-slate-400">
                          {(() => {
                            const d = post.match_info?.fixtureDate;
                            if (!d) return '-';
                            const dateObj = d.includes('T') || d.includes('Z') ? new Date(d) : new Date(d.replace(' ', 'T') + '+09:00');
                            return dateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                          })()}
                        </div>
                        <div className="text-[11px] font-medium text-slate-300">
                          {(() => {
                            const d = post.match_info?.fixtureDate;
                            if (!d) return '-';
                            const dateObj = d.includes('T') || d.includes('Z') ? new Date(d) : new Date(d.replace(' ', 'T') + '+09:00');
                            return dateObj.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Teams and Prediction Section */}
                    <div className="flex items-center gap-4">
                      {/* Logos */}
                      <div className="relative flex flex-col">
                        <div className="w-8 h-8 rounded-full bg-white shadow-sm border border-gray-100 flex items-center justify-center z-10 overflow-hidden">
                          <img src={post.match_info?.homeTeam.logo} alt="" className="w-6 h-6 object-contain" />
                        </div>
                        <div className="w-8 h-8 rounded-full bg-white shadow-sm border border-gray-100 flex items-center justify-center -mt-3 z-20 overflow-hidden">
                          <img src={post.match_info?.awayTeam.logo} alt="" className="w-6 h-6 object-contain" />
                        </div>
                      </div>

                      {/* Team Names */}
                      <div className="flex flex-col gap-2 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-800 leading-none truncate">{post.match_info?.homeTeam.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-800 leading-none truncate">{post.match_info?.awayTeam.name}</span>
                        </div>
                      </div>

                      <div className="flex-1" />

                      {/* Prediction Gauge Info */}
                      <div className="flex flex-col items-end gap-1.5 pr-4 min-w-[80px]">
                        {post.win_probability !== undefined && (
                          <>
                            <div className="flex items-center justify-between w-full gap-2">
                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Win Prob</span>
                              <span className="text-[11px] font-black text-indigo-600">{post.win_probability}%</span>
                            </div>
                            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-indigo-400 to-indigo-600 rounded-full transition-all duration-1000 ease-out"
                                style={{ width: `${post.win_probability}%` }}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Icon */}
                  <div className="shrink-0 flex items-center pl-4">
                    <ChevronRight size={18} className="text-slate-300 group-hover:text-rose-500 transition-colors" />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-20 text-center opacity-30">
            <Brain size={48} className="mx-auto mb-4" />
            <p className="text-sm font-black uppercase tracking-widest">분석 리포트가 없습니다</p>
          </div>
        )}
      </div>
    </div>
  );
};